import { readdir, readFile, stat } from "node:fs/promises";
import { basename, extname, join, relative } from "node:path";
import { parseFrontmatter } from "@vortex-os/core";
import type { IndexEntry, ScanOptions } from "./types.js";

const RESERVED_FILES: ReadonlySet<string> = new Set(["README.md", "_INDEX.md"]);
const H1_PATTERN = /^#\s+(.+?)\s*$/m;

/**
 * Scan a directory of markdown notes and produce one {@link IndexEntry}
 * per file, sorted by `relPath` ascending.
 *
 * - Files whose names are `README.md` or `_INDEX.md` are always skipped.
 * - Hidden entries (name starting with `.` or `_`, except `_TEMPLATE` which
 *   is still surfaced but can be excluded via `skipPrefixes: ["_TEMPLATE"]`)
 *   are not descended into when `recursive: true`.
 * - A missing directory returns an empty list rather than throwing.
 */
export async function scanDirectory(
  rootDir: string,
  opts: ScanOptions = {},
): Promise<readonly IndexEntry[]> {
  const skipFilenames = new Set([
    ...RESERVED_FILES,
    ...(opts.skipFilenames ?? []),
  ]);
  const skipPrefixes = opts.skipPrefixes ?? [];
  const recursive = opts.recursive ?? false;

  const out: IndexEntry[] = [];
  await walk(rootDir, rootDir, recursive, skipFilenames, skipPrefixes, out);
  return out.sort((a, b) => a.relPath.localeCompare(b.relPath));
}

async function walk(
  rootDir: string,
  currentDir: string,
  recursive: boolean,
  skipFilenames: ReadonlySet<string>,
  skipPrefixes: readonly string[],
  out: IndexEntry[],
): Promise<void> {
  let entries;
  try {
    entries = await readdir(currentDir, { withFileTypes: true });
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code === "ENOENT") return;
    throw e;
  }

  for (const dirent of entries) {
    const name = dirent.name;
    if (dirent.isDirectory()) {
      if (!recursive) continue;
      if (name.startsWith(".") || name.startsWith("_")) continue;
      await walk(
        rootDir,
        join(currentDir, name),
        recursive,
        skipFilenames,
        skipPrefixes,
        out,
      );
      continue;
    }
    if (!dirent.isFile()) continue;
    if (extname(name) !== ".md") continue;
    if (skipFilenames.has(name)) continue;

    const nameNoExt = basename(name, ".md");
    if (skipPrefixes.some((p) => nameNoExt.startsWith(p))) continue;

    const fullPath = join(currentDir, name);
    let info;
    try {
      info = await stat(fullPath);
      if (!info.isFile()) continue;
    } catch {
      continue;
    }
    const raw = await readFile(fullPath, "utf8");
    const { frontmatter, body } = parseFrontmatter<Record<string, unknown>>(raw);
    const title = extractTitle(body) ?? nameNoExt;
    const description = extractDescription(frontmatter, body);
    const type = stringField(frontmatter, "type");
    const updated =
      stringField(frontmatter, "updated") ?? stringField(frontmatter, "created");
    const scope = stringField(frontmatter, "scope");

    out.push({
      relPath: relative(rootDir, fullPath).split(/[\\/]/).join("/"),
      name: nameNoExt,
      title,
      description,
      type,
      updated,
      ...(scope ? { scope } : {}),
    });
  }
}

function extractTitle(body: string): string | undefined {
  const m = body.match(H1_PATTERN);
  if (!m) return undefined;
  return m[1]?.trim();
}

function extractDescription(
  frontmatter: Record<string, unknown>,
  body: string,
): string | undefined {
  const fm = stringField(frontmatter, "description");
  if (fm) return fm;
  // The frontmatter `description` is authoritative; otherwise take the first
  // line of real PROSE. Skip everything that is not prose so the index does not
  // pick up a table row, a code-fence marker, an HTML comment, a list bullet,
  // or a bare wiki-link/image as the "description" (all observed in real data).
  const lines = body.split(/\r?\n/);
  let inFence = false;
  let inComment = false;
  for (const line of lines) {
    const trimmed = line.trim();
    // Inside an HTML comment: only `-->` ends it (a ``` here is just text, so
    // check this BEFORE fence detection or a comment-embedded fence would stick).
    if (inComment) {
      if (trimmed.includes("-->")) inComment = false;
      continue;
    }
    // Inside a fenced code block: only a fence marker ends it.
    if (inFence) {
      if (/^(```|~~~)/.test(trimmed)) inFence = false;
      continue;
    }
    if (/^(```|~~~)/.test(trimmed)) { inFence = true; continue; } // open fence
    if (!trimmed) continue;
    if (trimmed.startsWith("<!--")) { // HTML comment (whole-line or multi-line)
      if (!trimmed.includes("-->")) inComment = true;
      continue;
    }
    if (trimmed.startsWith("#")) continue; // heading
    if (trimmed.startsWith(">")) continue; // blockquote
    if (trimmed.startsWith("|")) continue; // table row / separator
    if (/^<\/?[a-zA-Z!]/.test(trimmed)) continue; // HTML tag/block (NOT prose like "< 5 min")
    if (/^[-*+]\s/.test(trimmed)) continue; // unordered list item
    if (/^\d+[.)]\s/.test(trimmed)) continue; // ordered list item
    if (/^[-*_]([ \t]*[-*_]){2,}[ \t]*$/.test(trimmed)) continue; // horizontal rule (incl. spaced)
    if (/^!?\[\[[^\]]*\]\]$/.test(trimmed)) continue; // whole-line wiki embed/link
    if (/^!?\[[^\]]*\]\(.*\)$/.test(trimmed)) continue; // whole-line image / md link
    return trimmed.length > 160 ? `${trimmed.slice(0, 157)}...` : trimmed;
  }
  return undefined;
}

function stringField(
  obj: Record<string, unknown>,
  key: string,
): string | undefined {
  const v = obj[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}
